import React, { useState, useEffect } from 'react';
import {
    Typography, Box, Button, IconButton, CircularProgress,
    useTheme, useMediaQuery, TextField, Snackbar, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions,
    MenuItem, FormControl, InputLabel, Select, Switch
} from '@mui/material';
import { FiArrowLeft, FiPlus, FiServer, FiTrash2 , } from 'react-icons/fi';
import { MdStorage } from 'react-icons/md';
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import CloseIcon from '@mui/icons-material/Close';
import InstanceModal from './InstanceModal';

const ManageInstances = () => {
    const [instances, setInstances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const navigate = useNavigate();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [error, setError] = useState('');
const [instanceData, setInstanceData] = useState({
    name: '', logo: '', address1: '', address2: '', mobile: '', email: '', gst: '', website: '', status: 'Active'
});
const [editInstanceData, setEditInstanceData] = useState(null);

    useEffect(() => {
        fetchInstances();
    }, []);

     // ✅ Fetch all instances
    const fetchInstances = async () => {
        try {
            setLoading(true);
            const response = await api.get('/instances');
            console.log('✅ API Response (GET /instances):', response.data);
            setInstances(response.data);
        } catch (err) {
            console.error('❌ Error while fetching instances:', err);
            Swal.fire('Error', 'Failed to load instances', 'error');
        } finally {
            setLoading(false);
        }
    };

     // ✅ Add new instance
    const handleAddInstance = async () => {
        if (!instanceData.name.trim()) {
            Swal.fire('Warning', 'Please enter instance name', 'warning');
            return;
        }
        console.log('📤 Sending POST request to /instances with data:', instanceData);
        try {
            const response = await api.post('/instances', instanceData);
            console.log('✅ API Response (POST /instances):', response.data);
            setInstances(prev => [...prev, response.data]);
            setAddModalOpen(false);
            setInstanceData({ name: '', logo: '', address1: '', address2: '', mobile: '', email: '', gst: '', website: '', status: 'Active' });
            Swal.fire('Success', 'Instance added successfully', 'success');
        } catch (err) {
            console.error('❌ Error while adding instance:', err);
            Swal.fire('Error', 'Failed to add instance', 'error');
        }
    };


   // ✅ Edit existing instance
    const handleEditInstance = (row) => {
        console.log('✏️ Editing instance:', row);
        setEditInstanceData(row);
        setEditModalOpen(true);
    };


  // ✅ Save instance after editing
    const handleSaveEdit = async () => {
        if (!editInstanceData) return;
        console.log(`📤 Sending PUT request to /instances/${editInstanceData.instance_id}`, editInstanceData);
        try {
            const response = await api.put(`/instances/${editInstanceData.instance_id}`, editInstanceData);
            console.log('✅ API Response (PUT /instances/:id):', response.data);
            setInstances(prev => prev.map(i => i.instance_id === editInstanceData.instance_id ? editInstanceData : i));
            setEditModalOpen(false);
            setEditInstanceData(null);
            Swal.fire('Success', 'Instance updated successfully', 'success');
        } catch (err) {
            console.error('❌ Error while updating instance:', err);
            Swal.fire('Error', 'Failed to update instance', 'error');
        }
    };


  // ✅ Delete instance
    const handleDelete = async (id) => {
        console.log(`🗑️ Attempting to delete instance with ID: ${id}`);
        const confirmed = await Swal.fire({
            title: 'Are you sure?',
            text: 'This will delete the instance permanently!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });
        if (confirmed.isConfirmed) {
            try {
                const response = await api.delete(`/instances/${id}`);
                console.log('✅ API Response (DELETE /instances/:id):', response.data);
                setInstances(prev => prev.filter(i => i.id !== id));
                Swal.fire('Deleted!', 'Instance deleted successfully', 'success');
            } catch (err) {
                console.error('❌ Error while deleting instance:', err);
                Swal.fire('Error', 'Failed to delete instance', 'error');
            }
        }
    };

    const columns = [
        { field: 'instance_id', headerName: 'ID', width: 80 },
        { field: 'name', headerName: 'Instance Name', flex: 1 },
          { field: 'mobile', headerName: 'Mobile Number', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1 }, 
        {
            field: 'status', headerName: 'Status', width: 120,
            renderCell: (params) => <span style={{ color: params.value === 'Active' ? 'green' : 'red', fontWeight: 600 }}>{params.value}</span>
        },
        {
            field: 'actions', headerName: 'Actions', flex: 1, sortable: false,
            renderCell: (params) => (
                <Box display="flex" gap={1}>
                    {/* <Switch
                        checked={params.row.status === 'Active'}
                        onChange={() => handleEditInstance({ ...params.row, status: params.row.status === 'Active' ? 'Inactive' : 'Active' })}
                        color="primary"
                    /> */}
                    <IconButton color="primary" onClick={() => handleEditInstance(params.row)}>✏️</IconButton>
                    <IconButton color="error" onClick={() => handleDelete(params.row.instance_id)}><FiTrash2 /></IconButton>
                </Box>
            )
        }
    ];

    const CustomToolbar = () => (
        <GridToolbarContainer>
            <GridToolbarExport csvOptions={{ fileName: 'instances' }} />
            {selectedRows.length > 0 && (
                <Button variant="outlined" color="error" size="small" sx={{ ml: 2 }}>Delete Selected ({selectedRows.length})</Button>
            )}
        </GridToolbarContainer>
    );

    return (
        <Box p={2} sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box mb={3} display="flex" flexDirection={isSmallScreen ? 'column' : 'row'} justifyContent="space-between" alignItems="center" gap={2} textAlign={isSmallScreen ? 'center' : 'left'}>
                {/* Back Button */}
                <IconButton onClick={() => navigate(-1)} sx={{
                    borderRadius: '12px',
                    p: '8px 12px',
                    background: 'linear-gradient(135deg, #00bcd4, #007bff)',
                    color: '#fff',
                    '&:hover': { background: 'linear-gradient(135deg, #007bff, #00bcd4)', transform: 'scale(1.05)' }
                }}>
                    <FiArrowLeft />
                    {!isSmallScreen && <Typography variant="button" sx={{ ml: 1, fontWeight: 600 }}>Back</Typography>}
                </IconButton>

                {/* Title */}
               <Box display="flex" alignItems="center" justifyContent="center" gap={1} flex={1}>
    <Box sx={{
        background: 'linear-gradient(135deg, #007bff, #00d4ff)',
        borderRadius: '50%',
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'pulseIcon 1.6s infinite'
    }}>
        <MdStorage size={26} color="#fff"/>
    </Box>
    <Typography variant={isSmallScreen ? 'h6' : 'h5'} sx={{
        fontWeight: 'bold',
        background: 'linear-gradient(90deg, #007BFF, #00d4ff)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        textTransform: 'uppercase',
        letterSpacing: '1.2px',
        px: 1
    }}>
        Manage Instances
    </Typography>
</Box>


                {/* Add Instance Button */}
                <Button variant="contained" color="warning" startIcon={<FiPlus />} onClick={() => setAddModalOpen(true)}>
    Create Instance
</Button>
            </Box>

            {/* Table */}
            <Box sx={{ flexGrow: 1, height: 700 }}>
                {loading ? <CircularProgress /> : (
                    <DataGrid
                        rows={instances}
                        columns={columns}
                        pageSize={10}
                        rowsPerPageOptions={[10, 25, 50]}
                        onSelectionModelChange={(ids) => setSelectedRows(ids)}
                        components={{ Toolbar: CustomToolbar }}
                         getRowId={(row) => row.instance_id} 
                    />
                )}
            </Box>

            {/* Add Instance Dialog */}
           <InstanceModal
    open={addModalOpen}
    onClose={() => setAddModalOpen(false)}
    onSubmit={handleAddInstance}  
    instanceData={instanceData}
    setInstanceData={setInstanceData}
/>

            {/* Edit Instance Dialog */}
           <InstanceModal
    open={editModalOpen}
    onClose={() => {
        setEditModalOpen(false);
        setEditInstanceData(null);
    }}
    onSubmit={handleSaveEdit}
    instanceData={editInstanceData || { name: '', logo: '', address1: '', address2: '', mobile: '', email: '', gst: '', website: '', status: 'Active' }}
    setInstanceData={setEditInstanceData}
    title="Edit Instance"
    submitButtonText="Save Changes"
    submitButtonColor="primary"
/>

            <Snackbar open={!!error} autoHideDuration={3000} onClose={() => setError('')}>
                <Alert severity="error">{error}</Alert>
            </Snackbar>
        </Box>
        
    );
};

export default ManageInstances;
